import { h, toast, confirmDialog, tableScroll, rowAction, rowActions } from '../components/dom.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api } from '../api/client.js';
import { encode, Ecc } from '../vendor/qr.js';
import { drawQrCenterLogo } from '../lib/qr-logo.js';
import type { ShareLink, Profile } from '../api/types.js';
import { effectiveRevokedAt } from './links-lifecycle.js';
import { dateTimeEl, zonedInputToUtcISO, utcToZonedInputValue, tzShortLabel } from '../lib/timezone.js';
export { effectiveRevokedAt } from './links-lifecycle.js';

export async function renderLinks(_p: Record<string, string> = {}, query?: URLSearchParams): Promise<void> {
  const main = renderShell('/links');
  const createBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Create link');
  main.append(pageHead('Share Links', 'Every way people can reach your profiles — grouped by lifetime.', createBtn));

  const [linksRes, profilesRes] = await Promise.all([api.shareLinks(), api.profiles()]);
  const profileName = new Map(profilesRes.data.map((p) => [p.id, p.name]));

  // URL-driven profile filter (mirrors the Subscribers page) so the profile
  // page's "Active shared links" Edit/See-more links can deep-link here filtered.
  const filterProfile = query?.get('profile') || '';
  const pills = h('div', { class: 'pill-row', style: 'margin-bottom:16px' },
    h('span', { class: 'muted', style: 'align-self:center' }, h('small', {}, 'Filter by profile:')),
    linkPill('All', '#/links', !filterProfile));
  for (const p of profilesRes.data) pills.append(linkPill(p.name, '#/links?profile=' + encodeURIComponent(p.id), filterProfile === p.id));
  main.append(pills);

  const visibleLinks = filterProfile ? linksRes.data.filter((l) => l.profile_id === filterProfile) : linksRes.data;

  const groups: Record<string, ShareLink[]> = { temporary: [], long_lived: [], permanent: [] };
  const revoked: ShareLink[] = [];
  for (const l of visibleLinks) {
    // Revoked + naturally-expired links drop to the bottom "Revoked Invitations"
    // section. Disabled links stay in their active lifetime group.
    if (l.status === 'revoked' || l.status === 'expired') { revoked.push(l); continue; }
    (groups[l.lifetime_class || 'temporary'] ||= []).push(l);
  }

  const section = (title: string, sub: string, rows: ShareLink[]) => {
    const tbody = h('tbody');
    for (const l of rows) tbody.append(linkRow(l, profileName));
    if (!rows.length) tbody.append(h('tr', {}, h('td', { colspan: '8', class: 'muted' }, 'None.')));
    return h('div', {},
      h('h3', {}, title, ' ', h('small', {}, sub)),
      h('div', { class: 'card', style: 'padding:0;margin-bottom:26px' },
        tableScroll(h('table', { class: 'table' },
          h('thead', {}, h('tr', {},
            h('th', {}, 'QR'), h('th', {}, 'Label'), h('th', {}, 'Profile'), h('th', {}, 'Medium'),
            h('th', {}, 'Accepted'), h('th', {}, 'Expires'), h('th', {}, 'Status'), h('th', {}, 'Actions'))),
          tbody))));
  };

  main.append(
    section('Temporary', '(≤ 90 days)', groups.temporary),
    section('Long-lived', '(> 90 days)', groups.long_lived),
    section('Permanent', '(never expires)', groups.permanent),
  );
  main.append(revokedSection(revoked, profileName));
  main.append(h('p', { class: 'muted', style: 'margin-top:14px' }, h('small', { html:
    '<span class="badge badge-warn">Note</span> Permanent links require step-up authentication to create, and can be revoked instantly — keys rotate so printed/embedded copies go dark.' })));
  main.append(portalFooter());

  createBtn.addEventListener('click', () => openCreate(profilesRes.data));
}

function linkPill(label: string, href: string, active: boolean): HTMLElement {
  return h('a', { class: 'badge ' + (active ? 'badge-brand' : ''), href, style: 'text-decoration:none' }, label);
}

function linkRow(l: ShareLink, names: Map<string, string>): HTMLElement {
  const isRevoked = l.status === 'revoked';
  const revokeBtn = rowAction(isRevoked ? 'Revoked' : 'Revoke', async () => {
    if (!confirmDialog(`Revoke "${l.label || 'this link'}"? Keys rotate — any printed copy goes dark.`)) return;
    try { await api.revokeShareLink(l.id); toast('Link revoked', 'ok'); renderLinks(); }
    catch (err) { toast((err as Error).message, 'danger'); }
  }, { variant: 'danger', disabled: isRevoked });
  const expires = l.never_expires
    ? h('td', { html: '<strong>Never</strong><br><small class="muted">permanent</small>' })
    : h('td', {}, l.expires_at ? dateTimeEl(l.expires_at) : '', h('br'), h('small', { class: 'muted' }, relative(l.expires_at)));
  const qr = rowAction('QR', (e: Event) => { e.preventDefault(); showQR(l); }, { ariaLabel: 'View QR' });
  const copyLink = rowAction('Copy', (e: Event) => { e.preventDefault(); copyToClipboard(l.url); }, { title: l.url || '' });
  const editBtn = rowAction('Edit', (e: Event) => { e.preventDefault(); openEdit(l); }, { disabled: isRevoked });
  // Disable/Enable: a temporary, reversible switch separate from Revoke. Disabled
  // for revoked links (a revoked link can't be toggled). Does NOT touch expiry.
  const isDisabled = l.status === 'disabled';
  const toggleBtn = rowAction(isDisabled ? 'Enable' : 'Disable', async () => {
    try {
      await api.setShareLinkDisabled(l.id, !isDisabled);
      toast(isDisabled ? 'Link enabled' : 'Link disabled', 'ok');
      renderLinks();
    } catch (err) { toast((err as Error).message, 'danger'); }
  }, { disabled: isRevoked });
  const badgeClass = l.status === 'active' ? 'badge-ok' : 'badge-warn';
  const actions = isRevoked
    ? rowActions(copyLink, editBtn, revokeBtn)
    : rowActions(copyLink, editBtn, toggleBtn, revokeBtn);
  return h('tr', {},
    h('td', {}, qr),
    h('td', {}, l.label || '—'),
    h('td', {}, names.get(l.profile_id) || l.profile_id),
    h('td', {}, h('a', { href: '#', 'aria-label': 'View link / QR', style: 'text-decoration:none;cursor:pointer', onclick: (e: Event) => { e.preventDefault(); showQR(l); } }, h('span', { class: 'badge badge-brand', style: 'cursor:pointer' }, mediumLabel(l.medium)))),
    h('td', {}, h('span', { title: 'Invites accepted / max allowed (∞ = unlimited)' }, acceptsLabel(l))),
    expires,
    h('td', {}, h('span', { class: 'badge ' + badgeClass }, l.status || '—')),
    h('td', {}, actions));
}

// revokedSection renders the bottom "Revoked Invitations" block: revoked +
// naturally-expired links, grayed out but still clickable, with a "Remove all"
// bulk-purge button at the far right of the title line.
function revokedSection(rows: ShareLink[], names: Map<string, string>): HTMLElement {
  const removeAll = h('button', { class: 'btn btn-danger btn-sm' }, 'Remove all');
  if (!rows.length) removeAll.setAttribute('disabled', 'true');
  removeAll.addEventListener('click', async () => {
    if (!rows.length) return;
    // These links are ALREADY revoked/expired — purging dead links needs no confirm.
    try { await api.purgeRevokedShareLinks(); toast('Revoked links removed', 'ok'); renderLinks(); }
    catch (err) { toast((err as Error).message, 'danger'); }
  });
  const header = h('div', { style: 'display:flex;align-items:center;justify-content:space-between;gap:12px' },
    h('h3', {}, 'Revoked Invitations', ' ', h('small', {}, '(revoked or expired)')),
    removeAll);
  const tbody = h('tbody');
  for (const l of rows) tbody.append(revokedRow(l, names));
  if (!rows.length) tbody.append(h('tr', {}, h('td', { colspan: '6', class: 'muted' }, 'None.')));
  return h('div', {},
    header,
    h('div', { class: 'card', style: 'padding:0;margin-bottom:26px' },
      tableScroll(h('table', { class: 'table' },
        h('thead', {}, h('tr', {},
          h('th', {}, 'Label'), h('th', {}, 'Profile'), h('th', {}, 'Medium'),
          h('th', {}, 'Accepted'), h('th', {}, 'Revoked'), h('th', {}, 'Actions'))),
        tbody))));
}

// revokedRow renders a single grayed-out revoked/expired entry. The row (minus
// the action links) is clickable to open the detail/QR view (which shows EXPIRED
// in place of the QR but keeps the copy-URL experience).
function revokedRow(l: ShareLink, names: Map<string, string>): HTMLElement {
  const when = effectiveRevokedAt(l);
  const reactivateBtn = rowAction('Re-Activate', (e: Event) => { e.preventDefault(); openEdit(l, { reactivate: true }); });
  const removeBtn = rowAction('Remove', async (e: Event) => {
    e.preventDefault();
    try { await api.deleteShareLinkPermanent(l.id); toast('Link removed', 'ok'); renderLinks(); }
    catch (err) { toast((err as Error).message, 'danger'); }
  }, { variant: 'danger' });
  const open = (e: Event) => { e.preventDefault(); showQR(l); };
  const clickable = (el: HTMLElement) => { el.style.cursor = 'pointer'; el.addEventListener('click', open); return el; };
  const tr = h('tr', { style: 'opacity:.55' },
    clickable(h('td', {}, l.label || '—')),
    clickable(h('td', {}, names.get(l.profile_id) || l.profile_id)),
    clickable(h('td', {}, h('span', { class: 'badge badge-brand' }, mediumLabel(l.medium)))),
    clickable(h('td', {}, acceptsLabel(l))),
    clickable(h('td', {}, when ? dateTimeEl(when) : '—', when ? h('br') : '', when ? h('small', { class: 'muted' }, l.status === 'revoked' ? 'revoked' : 'expired') : '')),
    h('td', {}, rowActions(reactivateBtn, removeBtn)));
  return tr;
}

// acceptsLabel renders "accepted / max" for the Accepted column, using ∞ for
// unlimited (max_uses null/0). e.g. "7 / 50" or "3 / ∞".
function acceptsLabel(l: ShareLink): string {
  const used = l.use_count ?? l.uses ?? 0;
  const max = l.max_uses;
  return `${used} / ${max == null ? '∞' : max}`;
}

// openEdit lets the owner rename a link, adjust its expiration, and change the
// acceptance cap (max invites this link can accept). Expiry offers presets
function openEdit(l: ShareLink, opts: { reactivate?: boolean } = {}): void {
  const reactivate = !!opts.reactivate;
  const label = h('input', { class: 'input', value: l.label || '', placeholder: 'e.g. Expo booth QR' }) as HTMLInputElement;
  // In reactivate mode the user MUST choose a new expiry (no "Keep current"),
  // and the whole flow calls the reactivate endpoint instead of PATCH.
  const expiry = reactivate
    ? h('select', { class: 'select' },
        h('option', { value: '', selected: 'true', disabled: 'true' }, 'Choose a new expiration…'),
        h('option', { value: '1800000' }, 'Expire in 30 minutes'),
        h('option', { value: '86400000' }, 'Expire in 24 hours'),
        h('option', { value: '604800000' }, 'Expire in 7 days'),
        h('option', { value: '2592000000' }, 'Expire in 30 days'),
        h('option', { value: '7776000000' }, 'Expire in 90 days'),
        h('option', { value: '15552000000' }, 'Expire in 180 days'),
        h('option', { value: '31536000000' }, 'Expire in 1 year'),
        h('option', { value: 'permanent' }, 'Permanent (never expires)'),
        h('option', { value: 'custom' }, 'Custom — pick an expiration time…')) as HTMLSelectElement
    : h('select', { class: 'select' },
        h('option', { value: 'keep', selected: 'true' }, 'Keep current'),
        h('option', { value: '1800000' }, 'Expire in 30 minutes'),
        h('option', { value: '86400000' }, 'Expire in 24 hours'),
        h('option', { value: '604800000' }, 'Expire in 7 days'),
        h('option', { value: '2592000000' }, 'Expire in 30 days'),
        h('option', { value: '7776000000' }, 'Expire in 90 days'),
        h('option', { value: '15552000000' }, 'Expire in 180 days'),
        h('option', { value: '31536000000' }, 'Expire in 1 year'),
        h('option', { value: 'permanent' }, 'Permanent (never expires)'),
        h('option', { value: 'custom' }, 'Custom — pick an expiration time…')) as HTMLSelectElement;
  const customAt = h('input', { class: 'input', type: 'datetime-local' }) as HTMLInputElement;
  const customField = field('Expires at (' + tzShortLabel() + ')', customAt);
  const customHint = h('small', { class: 'muted' }, 'Enter the time in your timezone (' + tzShortLabel() + '). Saved and sent to the server in UTC.');
  customField.style.display = 'none';
  customHint.style.display = 'none';
  expiry.addEventListener('change', () => {
    const show = expiry.value === 'custom';
    customField.style.display = show ? '' : 'none';
    customHint.style.display = show ? '' : 'none';
    if (show && !customAt.value) {
      const seedIso = l.expires_at || new Date(Date.now() + 7 * 864e5).toISOString();
      customAt.value = utcToZonedInputValue(seedIso);
    }
  });

  // Max accepts: how many invites this link can accept. Empty or 0 => unlimited.
  const maxUses = h('input', {
    class: 'input', type: 'number', min: '0', step: '1',
    value: l.max_uses == null ? '' : String(l.max_uses),
    placeholder: '0 = unlimited',
  }) as HTMLInputElement;
  const usedNow = l.use_count ?? l.uses ?? 0;
  const maxHelp = h('small', { class: 'muted' }, `${usedNow} already accepted. Set 0 (or blank) for unlimited.`);

  // Approval mode: editable post-create. Changing it affects only FUTURE
  // redemptions — already-active/pending subscriptions are untouched.
  const curApproval: 'auto' | 'explicit' = l.approval_mode === 'explicit' ? 'explicit' : 'auto';
  const approval = h('select', { class: 'select' },
    h('option', { value: 'auto', ...(curApproval === 'auto' ? { selected: 'true' } : {}) }, 'Automatic — accepted instantly'),
    h('option', { value: 'explicit', ...(curApproval === 'explicit' ? { selected: 'true' } : {}) }, 'Manual — you approve each person')) as HTMLSelectElement;
  const approvalHelp = h('small', { class: 'muted' }, 'Automatic activates subscriptions on redeem; Manual queues each person for your approval. Only affects future redemptions.');

  const overlay = h('div', { class: 'modal-overlay', onclick: () => overlay.remove() });
  const save = h('button', { class: 'btn btn-primary btn-block' }, reactivate ? 'Re-Activate link' : 'Save changes');
  save.addEventListener('click', async () => {
    const patch: { label?: string; expires_at?: string | null; permanent?: boolean; max_uses?: number; approval_mode?: 'auto' | 'explicit' } = { label: label.value.trim() };
    // max accepts: blank => unlimited (0). Reject negatives / below already-used.
    const raw = maxUses.value.trim();
    const nextMax = raw === '' ? 0 : Number(raw);
    if (!Number.isInteger(nextMax) || nextMax < 0) { toast('Max accepts must be 0 or a positive whole number', 'warn'); return; }
    if (nextMax !== 0 && nextMax < usedNow) { toast(`Max can't be below the ${usedNow} already accepted`, 'warn'); return; }
    patch.max_uses = nextMax;
    // approval mode: include only when it differs from the current value.
    const nextApproval = approval.value as 'auto' | 'explicit';
    if (nextApproval !== curApproval) patch.approval_mode = nextApproval;
    if (expiry.value === 'permanent') {
      patch.permanent = true;
    } else if (expiry.value === 'custom') {
      if (!customAt.value) { toast('Pick an expiration time', 'warn'); return; }
      const iso = zonedInputToUtcISO(customAt.value);
      const whenMs = Date.parse(iso);
      if (isNaN(whenMs) || whenMs <= Date.now()) { toast('Expiration must be in the future', 'warn'); return; }
      patch.expires_at = iso;
    } else if (expiry.value !== 'keep') {
      patch.expires_at = new Date(Date.now() + Number(expiry.value)).toISOString();
    }
    // Reactivate REQUIRES an explicit expiry choice (permanent or a time).
    if (reactivate && !patch.permanent && !patch.expires_at) {
      toast('Choose a new expiration to re-activate', 'warn'); return;
    }
    try {
      if (reactivate) {
        await api.reactivateShareLink(l.id, patch);
        overlay.remove();
        toast('Link re-activated', 'ok');
      } else {
        await api.updateShareLink(l.id, patch);
        overlay.remove();
        toast('Link updated', 'ok');
      }
      renderLinks();
    } catch (e) {
      toast((e as Error).message, 'warn');
    }
  });
  const card = h('div', { class: 'card', style: 'max-width:440px;width:100%', onclick: (e: Event) => e.stopPropagation() },
    h('h3', {}, reactivate ? 'Re-activate share link' : 'Edit share link'),
    field('Label', label), field('Expiration', expiry), customField, customHint,
    field('Max accepts', maxUses), maxHelp,
    field('Approval mode', approval), approvalHelp, save);
  overlay.append(card); document.body.append(overlay);
}

// Copy an invite URL to the clipboard with a graceful toast (invite links are
// /id/<alias>/invite/<code>, server-provided — no client-built /s/ tokens).
function copyToClipboard(url?: string): void {
  if (!url) { toast('No link URL available yet.', 'warn'); return; }
  const done = () => toast('Link copied to clipboard', 'ok');
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(url).then(done).catch(() => toast(url, 'warn'));
  } else {
    toast(url, 'warn');
  }
}

function mediumLabel(m?: string): string {
  return ({ qr_screen: 'QR', business_card: 'Business card', rfid_card: 'RFID', nfc_tag: 'NFC', email_sig: 'Link', poster: 'Poster', other: 'Link' } as Record<string, string>)[m || 'other'] || 'Link';
}
function relative(iso?: string | null): string {
  if (!iso) return '';
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 864e5);
  return days < 0 ? 'expired' : `in ${days}d`;
}

// Render a crisp, scannable QR into an HTMLCanvasElement. Hi-DPI aware
// (scaled by devicePixelRatio) with a 4-module quiet zone per the QR spec.
// Uses HIGH ECC (30% recovery) so the centered VirtualID logo stays scannable.
function qrCanvas(url: string, cssSize = 240): HTMLCanvasElement {
  const { size, modules } = encode(url, Ecc.HIGH);
  const quiet = 4;
  const total = size + quiet * 2;
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const scale = Math.max(1, Math.floor((cssSize * dpr) / total));
  const px = total * scale;
  const canvas = h('canvas', { width: px, height: px, style: `width:${cssSize}px;height:${cssSize}px;border-radius:8px` }) as HTMLCanvasElement;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, px, px);
  ctx.fillStyle = '#000';
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++)
      if (modules[y][x]) ctx.fillRect((x + quiet) * scale, (y + quiet) * scale, scale, scale);
  drawQrCenterLogo(ctx, px);
  return canvas;
}

function showQR(l: ShareLink): void {
  const overlay = h('div', { class: 'modal-overlay', onclick: () => overlay.remove() });
  const url = (l.url || '').trim();

  // Revoked/expired links don't render a QR — show "EXPIRED" in its place, but
  // still keep the URL + copy-URL experience below.
  const isDead = l.status === 'revoked' || l.status === 'expired';

  let qrEl: HTMLElement;
  if (isDead) {
    qrEl = h('div', { class: 'muted', style: 'margin:24px auto;padding:40px 0' }, h('strong', { style: 'font-size:22px;letter-spacing:2px' }, 'EXPIRED'));
  } else if (url) {
    try { qrEl = qrCanvas(url); }
    catch { qrEl = h('p', { class: 'muted', style: 'margin:24px auto' }, 'Could not render QR'); }
  } else {
    qrEl = h('div', { class: 'muted', style: 'margin:24px auto' }, h('strong', {}, 'No URL'), h('br'), h('small', {}, 'This link has no URL to encode yet.'));
  }

  const copyBtn = h('button', { class: 'btn btn-ghost btn-sm', style: 'margin-right:8px',
    onclick: async () => {
      try { await navigator.clipboard.writeText(url); toast('URL copied', 'ok'); }
      catch { toast('Copy failed', 'warn'); }
    } }, 'Copy URL');

  const card = h('div', { class: 'card', style: 'text-align:center;max-width:320px', onclick: (e: Event) => e.stopPropagation() },
    h('h3', {}, l.label || 'Share link'),
    h('div', { style: 'margin:16px auto;display:flex;justify-content:center' }, qrEl),
    h('p', { class: 'muted', style: 'word-break:break-all' }, h('small', {}, url || '—')),
    h('div', { style: 'margin-top:12px' },
      url ? copyBtn : null,
      h('button', { class: 'btn btn-ghost btn-sm', onclick: () => overlay.remove() }, 'Close')));
  overlay.append(card); document.body.append(overlay);
}

function openCreate(profiles: Profile[]): void {
  const profSel = h('select', { class: 'select' }, ...profiles.map((p) => h('option', { value: p.id }, p.name))) as HTMLSelectElement;
  // Lifetime presets (ms from now) + 'custom' (explicit datetime) + 'permanent' (never expires).
  const lifetime = h('select', { class: 'select' },
    h('option', { value: '1800000' }, '30 minutes'),
    h('option', { value: '86400000', selected: 'true' }, '24 hours'),
    h('option', { value: '604800000' }, '7 days'),
    h('option', { value: '2592000000' }, '30 days'),
    h('option', { value: '7776000000' }, '90 days'),
    h('option', { value: '15552000000' }, '180 days'),
    h('option', { value: '31536000000' }, '1 year'),
    h('option', { value: 'permanent' }, 'Permanent (never expires — step-up)'),
    h('option', { value: 'custom' }, 'Custom — pick an expiration time…')) as HTMLSelectElement;
  // Explicit expiration picker, shown only when lifetime = custom.
  const customAt = h('input', { class: 'input', type: 'datetime-local' }) as HTMLInputElement;
  const customField = field('Expires at (' + tzShortLabel() + ')', customAt);
  const customHint = h('small', { class: 'muted' }, 'Enter the time in your timezone (' + tzShortLabel() + '). Saved and sent to the server in UTC.');
  customField.style.display = 'none';
  customHint.style.display = 'none';
  lifetime.addEventListener('change', () => {
    const show = lifetime.value === 'custom';
    customField.style.display = show ? '' : 'none';
    customHint.style.display = show ? '' : 'none';
    if (show && !customAt.value) {
      // Default the picker to 7 days out (in the user's display tz) for convenience.
      customAt.value = utcToZonedInputValue(new Date(Date.now() + 7 * 864e5).toISOString());
    }
  });
  const label = h('input', { class: 'input', placeholder: 'e.g. Expo booth QR' }) as HTMLInputElement;
  const approval = h('select', { class: 'select' },
    h('option', { value: 'auto' }, 'Automatic — anyone with the link is added'),
    h('option', { value: 'explicit' }, 'Explicit — I approve each subscriber')) as HTMLSelectElement;
  // Max accepts: how many invites this link may accept. Default follows lifetime —
  // 50 for a time-limited invite, unlimited (blank) for a permanent link. The user
  // can override; we only auto-fill while they haven't touched the field.
  const maxUses = h('input', { class: 'input', type: 'number', min: '0', step: '1', value: '50', placeholder: '0 = unlimited' }) as HTMLInputElement;
  let maxTouched = false;
  maxUses.addEventListener('input', () => { maxTouched = true; });
  const syncMaxDefault = () => {
    if (maxTouched) return;
    maxUses.value = lifetime.value === 'permanent' ? '' : '50';
  };
  lifetime.addEventListener('change', syncMaxDefault);

  const overlay = h('div', { class: 'modal-overlay', onclick: () => overlay.remove() });
  const create = h('button', { class: 'btn btn-primary btn-block' }, 'Create link');
  create.addEventListener('click', async () => {
    // Resolve the chosen lifetime into an absolute expiry (or null = permanent).
    let expires_at: string | null;
    if (lifetime.value === 'permanent') {
      expires_at = null;
    } else if (lifetime.value === 'custom') {
      if (!customAt.value) { toast('Pick an expiration time', 'warn'); return; }
      const iso = zonedInputToUtcISO(customAt.value);
      const whenMs = Date.parse(iso);
      if (isNaN(whenMs) || whenMs <= Date.now()) { toast('Expiration must be in the future', 'warn'); return; }
      expires_at = iso;
    } else {
      expires_at = new Date(Date.now() + Number(lifetime.value)).toISOString();
    }
    try {
      // Resolve max accepts: blank => unlimited (0 to the API).
      const rawMax = maxUses.value.trim();
      const maxVal = rawMax === '' ? 0 : Number(rawMax);
      if (!Number.isInteger(maxVal) || maxVal < 0) { toast('Max accepts must be 0 or a positive whole number', 'warn'); return; }
      // Always QR + link (kind=qr_link, medium=qr_screen). Permanent needs step-up (spec).
      const created = await api.createShareLink({ profile_id: profSel.value, kind: 'qr_link', expires_at, approval_mode: approval.value as 'auto' | 'explicit', label: label.value || undefined, medium: 'qr_screen', max_uses: maxVal || undefined });
      overlay.remove();
      toast('Link created', 'ok');
      if (created.url) showQR(created);
      renderLinks();
    } catch (e) {
      const msg = (e as Error).message;
      toast(msg.includes('step') ? 'Permanent links need step-up — confirm with passkey (TODO)' : msg, 'warn');
    }
  });
  const card = h('div', { class: 'card', style: 'max-width:440px;width:100%', onclick: (e: Event) => e.stopPropagation() },
    h('h3', {}, 'Create share link'),
    h('p', { class: 'muted', style: 'margin:-4px 0 14px' }, h('small', {}, 'Generates a QR code and a shareable link.')),
    field('Profile', profSel), field('Lifetime', lifetime), customField, customHint, field('Label', label),
    field('Approval', approval), field('Max accepts', maxUses), create);
  overlay.append(card); document.body.append(overlay);
}

function field(label: string, control: HTMLElement): HTMLElement {
  return h('div', { class: 'field' }, h('label', { class: 'label' }, label), control);
}
